import { Option, Select } from "@deere/fuel-react";
import { Box, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TFunction } from "i18next";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

export type SelectMultipleProps = {
  options: string[];
  initialValues: string[];
  onChange: (values: string[]) => void;
  disabledFor?: string;
  enableSelectionFromButton?: boolean;
  sizeLarge?: boolean;
  disabled?: boolean;
  showError?: boolean;
};

const SelectMultiple = ({
  options,
  initialValues,
  onChange,
  disabledFor,
  enableSelectionFromButton = false,
  sizeLarge = false,
  disabled = false,
  showError = false,
}: SelectMultipleProps) => {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const selectWrapperRef = useRef<HTMLDivElement>(null);

  const defaultLinkedSelectedValue = (
    typeof initialValues === "string" ? [initialValues] : initialValues
  ).map((option) => option.split(" ")[0]);

  const [tempSelection, setTempSelection] = useState<string[]>(
    defaultLinkedSelectedValue
  );
  const [dropdownContainer, setDropdownContainer] =
    useState<HTMLElement | null>(null);
  const [selectKey, setSelectKey] = useState(0);

  // Holds the last applied values — kept in a ref to avoid stale closures
  // inside the MutationObserver callback.
  const committedRef = useRef<string[]>(defaultLinkedSelectedValue);
  // Flipped to true in handleApplySelection before the dropdown closes so
  // findDropdown knows not to discard the selection.
  const closedByApplyRef = useRef(false);
  // Tracks whether the dropdown is currently open.
  const isOpenRef = useRef(false);

  useEffect(() => {
    if (!enableSelectionFromButton) {
      setDropdownContainer(null);
      return () => {};
    }

    const findDropdown = () => {
      const selectInput =
        selectWrapperRef.current?.querySelector('[role="combobox"]');
      const isExpanded = selectInput?.getAttribute("aria-expanded") === "true";

      if (isExpanded) {
        isOpenRef.current = true;
        const listbox = document.querySelector('[role="listbox"]');

        if (listbox && selectWrapperRef.current) {
          setDropdownContainer(listbox as HTMLElement);

          const wrapperWidth =
            selectWrapperRef.current.getBoundingClientRect().width;
          const listboxParent = listbox.parentElement;
          if (listboxParent) {
            listboxParent.style.minWidth = `${wrapperWidth}px`;
            listboxParent.style.width = "auto";
          }

          (listbox as HTMLElement).style.paddingBottom = "0";
        }
      } else if (isOpenRef.current) {
        // Dropdown just closed
        isOpenRef.current = false;
        setDropdownContainer(null);

        if (!closedByApplyRef.current) {
          // Closed without applying — discard pending selection
          setTempSelection(committedRef.current);
          setSelectKey((k) => k + 1);
        }
        closedByApplyRef.current = false;
      }
    };

    const observer = new MutationObserver(findDropdown);
    observer.observe(document.body, { childList: true, subtree: true });
    findDropdown();

    return () => {
      observer.disconnect();
      setDropdownContainer(null);
    };
  }, [enableSelectionFromButton]);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const values = Array.from(event.target.selectedOptions).map(
      (option) => option.label.split(" ")[0]
    );

    if (enableSelectionFromButton) {
      setTempSelection(values);
    } else {
      onChange(values);
    }
  };

  const handleApplySelection = () => {
    closedByApplyRef.current = true;
    committedRef.current = tempSelection;
    onChange(tempSelection);

    const selectInput =
      selectWrapperRef.current?.querySelector('[role="combobox"]');
    (selectInput as HTMLElement | null)?.click();
  };

  const nonDisabledOptions = options.filter(
    (option) =>
      disabledFor === "" ||
      disabledFor === undefined ||
      (disabledFor && !option.includes(disabledFor))
  );

  return (
    <>
      <Box
        ref={selectWrapperRef}
        sx={{
          width: sizeLarge ? "unset" : "70%",
          ...(showError
            ? {
                "& [role='combobox']": {
                  borderColor: theme.tokens.FuelColorAlertErrorIcon,
                  boxShadow: `0 0 0 2px ${theme.tokens.FuelColorAlertErrorIcon}`,
                  borderRadius: theme.tokens.FuelBorderRadiusSm,
                },
              }
            : {}),
        }}
        data-testid={
          showError
            ? "multiple-selection-error-indicator"
            : "multiple-selection"
        }
      >
        <Select
          key={selectKey}
          hasSearch
          hasSelectAll
          data-testid="select-value"
          multiple
          onChange={handleChange}
          defaultValue={committedRef.current}
          disabled={disabled}
          textMap={getTranslationsForSelectComponent(t)}
        >
          {nonDisabledOptions.map((option) => (
            <Option
              key={option.split(" ")[0]}
              value={option.split(" ")[0]}
              data-testid={`select-${option}`}
            >
              {option}
            </Option>
          ))}
        </Select>
      </Box>

      {enableSelectionFromButton &&
        dropdownContainer &&
        createPortal(
          <Box
            sx={{
              padding: theme.tokens.FuelSpacing3,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              position: "sticky",
              bottom: 0,
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleApplySelection}
              data-testid="apply-selection-button"
              sx={{
                textTransform: "none",
                fontWeight: "semibold",
                color: theme.palette.primary.main,
                borderColor: theme.tokens.FuelColorBackgroundWhite,
                backgroundColor: "transparent",
                width: "128px",
                height: "36px",
                minWidth: "128px",
                padding: theme.tokens.FuelSpacing2,
                fontSize: theme.tokens.FuelFontSizeSm,
                lineHeight: theme.tokens.FuelLineHeightSm,
                "&:hover": {
                  backgroundColor: "#F7FAF7",
                },
                "&:active": {
                  backgroundColor: "#E7EFE6",
                },
                "&:focus": {
                  outline: "none",
                },
                "&:focus-visible": {
                  outline: `2px solid ${theme.tokens.FuelColorTextActionPrimary}`,
                  outlineOffset: "2px",
                },
              }}
            >
              {t("header.selectDealerAccount.applySelection")}
            </Button>
          </Box>,
          dropdownContainer
        )}
    </>
  );
};

export default SelectMultiple;

const getTranslationsForSelectComponent = (t: TFunction) => ({
  placeholderMultiple:
    t("header.selectDealerAccount.placeholder.withMultipleOptions") ?? "",
  placeholderSingle:
    t("header.selectDealerAccount.placeholder.withSingleOption") ?? "",
  selectedMultipleText: (count: number) =>
    t("header.selectDealerAccount.placeholder.withMultipleOptionsSelected", {
      selectedCount: count,
    }) ?? "",
  dropdownSearchPlaceholder: t("searchBox.searchPlaceholder") ?? "",
  dropdownSelectAll: t("header.selectDealerAccount.selectAll") ?? "",
  dropdownDeselectAll: t("header.selectDealerAccount.deselectAll") ?? "",
});
